import React, { useState, useEffect, useRef, useCallback } from "react";

/* ─────────────────────────────────────────────
   PIN_LENGTH – change here to adjust globally
───────────────────────────────────────────── */
const PIN_LENGTH = 6;

/* ─────────────────────────────────────────────
   Pattern grid positions  (3 × 3)
   Each dot: { id, cx, cy } in SVG coordinates
───────────────────────────────────────────── */
const DOTS = [
  { id: 0, cx: 60,  cy: 60  },
  { id: 1, cx: 150, cy: 60  },
  { id: 2, cx: 240, cy: 60  },
  { id: 3, cx: 60,  cy: 150 },
  { id: 4, cx: 150, cy: 150 },
  { id: 5, cx: 240, cy: 150 },
  { id: 6, cx: 60,  cy: 240 },
  { id: 7, cx: 150, cy: 240 },
  { id: 8, cx: 240, cy: 240 },
];

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function padTime(n) { return String(n).padStart(2, "0"); }

function formatTime(d) {
  return `${padTime(d.getHours())}:${padTime(d.getMinutes())}:${padTime(d.getSeconds())}`;
}

function formatDate(d) {
  return d.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
  });
}

/* hit-test: which dot is close to (x, y) */
function hitDot(x, y, svgRect) {
  const scaleX = 300 / svgRect.width;
  const scaleY = 300 / svgRect.height;
  const sx = (x - svgRect.left) * scaleX;
  const sy = (y - svgRect.top)  * scaleY;
  return DOTS.find((d) => Math.hypot(d.cx - sx, d.cy - sy) < 30) ?? null;
}

/* ═══════════════════════════════════════════
   PatternUnlock sub-component
═══════════════════════════════════════════ */
function PatternUnlock({ storedPattern, onUnlock, error, setError }) {
  const svgRef        = useRef(null);
  const [pattern, setPattern]     = useState([]);
  const [cursor,  setCursor]      = useState(null);   // live line endpoint
  const [failed,  setFailed]      = useState(false);
  const drawing = useRef(false);

  const resetPattern = () => {
    setPattern([]);
    setCursor(null);
    drawing.current = false;
    setFailed(false);
    setError("");
  };

  const submitPattern = useCallback((seq) => {
    const str = seq.join("-");
    if (str === storedPattern) {
      onUnlock();
    } else {
      setFailed(true);
      setError("Incorrect pattern. Try again.");
      setTimeout(resetPattern, 800);
    }
  }, [storedPattern, onUnlock]);

  /* ── pointer events ── */
  const getPos = (e) => {
    if (e.touches) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const onStart = (e) => {
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const { x, y } = getPos(e);
    const dot = hitDot(x, y, rect);
    if (!dot) return;
    drawing.current = true;
    setFailed(false);
    setError("");
    setPattern([dot.id]);
    setCursor({ x, y });
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const { x, y } = getPos(e);
    setCursor({ x, y });
    const dot = hitDot(x, y, rect);
    if (dot && !pattern.includes(dot.id)) {
      setPattern((p) => [...p, dot.id]);
    }
  };

  const onEnd = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    setCursor(null);
    if (pattern.length >= 4) {
      submitPattern(pattern);
    } else {
      setError("Connect at least 4 dots.");
      setTimeout(resetPattern, 800);
    }
  };

  /* ── convert screen cursor to SVG coords ── */
  const toSVG = (screenX, screenY) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (screenX - rect.left) * (300 / rect.width),
      y: (screenY - rect.top)  * (300 / rect.height),
    };
  };

  const cursorSVG = cursor ? toSVG(cursor.x, cursor.y) : null;

  /* build polyline points from pattern */
  const linePoints = pattern.map((id) => {
    const d = DOTS[id];
    return `${d.cx},${d.cy}`;
  }).join(" ");

  const dotColor = failed ? "#ef4444" : "#22d3ee";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <svg
        ref={svgRef}
        viewBox="0 0 300 300"
        style={{ width: 240, height: 240, touchAction: "none", cursor: "crosshair" }}
        onMouseDown={onStart}  onMouseMove={onMove}  onMouseUp={onEnd}
        onTouchStart={onStart} onTouchMove={onMove}  onTouchEnd={onEnd}
      >
        {/* connecting lines */}
        {pattern.length > 1 && (
          <polyline
            points={linePoints}
            fill="none"
            stroke={dotColor}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity="0.6"
          />
        )}

        {/* live cursor line */}
        {cursorSVG && pattern.length > 0 && (
          <line
            x1={DOTS[pattern[pattern.length - 1]].cx}
            y1={DOTS[pattern[pattern.length - 1]].cy}
            x2={cursorSVG.x}
            y2={cursorSVG.y}
            stroke={dotColor}
            strokeWidth="2"
            opacity="0.4"
          />
        )}

        {/* dots */}
        {DOTS.map((d) => {
          const active = pattern.includes(d.id);
          return (
            <g key={d.id}>
              <circle cx={d.cx} cy={d.cy} r={22} fill="rgba(255,255,255,0.04)" />
              <circle
                cx={d.cx} cy={d.cy} r={active ? 12 : 8}
                fill={active ? dotColor : "rgba(148,163,184,0.4)"}
                style={{ transition: "all 0.15s ease" }}
              />
              {active && (
                <circle cx={d.cx} cy={d.cy} r={18}
                  fill="none" stroke={dotColor} strokeWidth="1.5" opacity="0.4" />
              )}
            </g>
          );
        })}
      </svg>

      {error && (
        <p style={{ color: "#ef4444", fontSize: 13, margin: 0 }}>{error}</p>
      )}

      {pattern.length > 0 && !drawing.current && !failed && (
        <button onClick={resetPattern} style={styles.ghostBtn}>Clear</button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Main LockScreen
═══════════════════════════════════════════ */
export default function LockScreen({ onUnlock, appName = "ChatFlow" }) {
  const storedType    = localStorage.getItem("app-lock-type")    || "pin";
  const storedPin     = localStorage.getItem("app-lock-pin")     || "";
  const storedPattern = localStorage.getItem("app-lock-pattern") || "";

  const [tab,   setTab]   = useState(storedType);
  const [pin,   setPin]   = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [time,  setTime]  = useState(new Date());

  /* live clock */
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ── PIN logic ── */
  const appendDigit = (d) => {
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + d;
    setPin(next);
    setError("");

    if (next.length === PIN_LENGTH) {
      setTimeout(() => {
        if (next === storedPin) {
          onUnlock();
        } else {
          setShake(true);
          setError("Incorrect PIN. Try again.");
          setPin("");
          setTimeout(() => setShake(false), 500);
        }
      }, 150);
    }
  };

  const backspace = () => {
    setPin((p) => p.slice(0, -1));
    setError("");
  };

  const handleForgot = (e) => {
    e.preventDefault();
    alert("A reset link has been sent to your recovery email.");
  };

  /* ── numpad layout ── */
 const PAD = [
  [{ d: "1", sub: "" }, { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" }],
  [{ d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" }],
  [{ d: "7", sub: "PQRS" }, { d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" }],
  [{ d: "*" }, { d: "0", sub: "" }, { d: "⌫" }],
];
  return (
    <div style={styles.overlay}>
      {/* ── Background radial glow ── */}
      <div style={styles.glow1} />
      <div style={styles.glow2} />

      {/* ── Clock ── */}
      <div style={styles.clockWrap}>
        <div style={styles.clockDate}>{formatDate(time)}</div>
      </div>

      {/* ── Card ── */}
      <div style={styles.card}>
        {/* App icon */}
        <div style={styles.iconWrap}>
          <i className="fa-solid fa-comment-dots" style={{ fontSize: 26, color: "#fff" }} />
        </div>

        <h2 style={styles.title}>{appName} is Locked</h2>

        <p style={styles.subtitle}>
          {tab === "pin"
            ? "Enter your PIN to unlock"
            : "Draw your unlock pattern · Connect dots top-to-bottom"}
        </p>

        {/* ── Tabs ── */}
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tabBtn, ...(tab === "pin" ? styles.tabActive : {}) }}
            onClick={() => { setTab("pin"); setPin(""); setError(""); }}
          >
            <i className="fa-solid fa-hashtag" style={{ marginRight: 6 }} />PIN
          </button>
          <button
            style={{ ...styles.tabBtn, ...(tab === "pattern" ? styles.tabActive : {}) }}
            onClick={() => { setTab("pattern"); setError(""); }}
          >
            <i className="fa-regular fa-circle" style={{ marginRight: 6 }} />Pattern
          </button>
        </div>

        {/* ── PIN mode ── */}
        {tab === "pin" && (
          <>
            {/* dot indicators */}
            <div style={{ ...styles.dotRow, animation: shake ? "lockShake 0.4s ease" : "none" }}>
              {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.pinDot,
                    ...(i < pin.length ? styles.pinDotFilled : {}),
                  }}
                />
              ))}
            </div>

            {error && <p style={styles.errorTxt}>{error}</p>}

            {/* numpad */}
            <div style={styles.numpad}>
              {PAD.map((row, ri) => (
                <div key={ri} style={styles.numRow}>
                  {row.map((cell) => {
                    if (cell.d === "*") {
  return (
    <button
      key="star"
      style={styles.numBtn}
      disabled
    >
      <span style={styles.numMain}>*</span>
    </button>
  );
}
                    if (cell.d === "⌫") {
                      return (
                        <button key="bs" style={styles.numBtn} onClick={backspace}>
                          <i className="fa-solid fa-delete-left" style={{ fontSize: 20, color: "#94a3b8" }} />
                        </button>
                      );
                    }
                    return (
                      <button key={cell.d} style={styles.numBtn} onClick={() => appendDigit(cell.d)}>
                        <span style={styles.numMain}>{cell.d}</span>
                        {cell.sub && <span style={styles.numSub}>{cell.sub}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Pattern mode ── */}
        {tab === "pattern" && (
          <PatternUnlock
            storedPattern={storedPattern}
            onUnlock={onUnlock}
            error={error}
            setError={setError}
          />
        )}

        {/* ── Footer ── */}
        {/* <div style={styles.footer}>
          <a href="#" onClick={handleForgot} style={styles.forgotLink}>
            Forgot lock? Reset via email
          </a>
         
        </div> */}
      </div>

      {/* ── keyframe animations injected once ── */}
      <style>{`
        @keyframes lockShake {
          0%,100%{ transform:translateX(0) }
          20%    { transform:translateX(-8px) }
          40%    { transform:translateX(8px) }
          60%    { transform:translateX(-5px) }
          80%    { transform:translateX(5px) }
        }
        @keyframes lockFadeIn {
          from { opacity:0; transform:translateY(20px) }
          to   { opacity:1; transform:translateY(0) }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Styles object
───────────────────────────────────────────── */
const styles = {
  overlay: {
    position:        "fixed",
    inset:           0,
    zIndex:          9999,
    background:      "linear-gradient(135deg, #0d1117 0%, #0f172a 50%, #0d1117 100%)",
    display:         "flex",
    flexDirection:   "column",
    alignItems:      "center",
    justifyContent:  "center",
    gap:             24,
    fontFamily:      "'Segoe UI', system-ui, sans-serif",
    overflow:        "hidden",
  },

  /* glow blobs */
  glow1: {
    position:   "absolute",
    top:        "-10%",
    left:       "20%",
    width:      400,
    height:     400,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  glow2: {
    position:   "absolute",
    bottom:     "-5%",
    right:      "10%",
    width:      320,
    height:     320,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },

  /* clock */
  clockWrap: {
    textAlign:  "center",
    color:      "#f1f5f9",
    animation:  "lockFadeIn 0.5s ease both",
  },
  clockTime: {
    fontSize:     "clamp(36px, 8vw, 52px)",
    fontWeight:   700,
    letterSpacing: "2px",
    lineHeight:   1.1,
    textShadow:   "0 0 30px rgba(34,211,238,0.3)",
  },
  clockDate: {
    fontSize:     14,
    color:        "#94a3b8",
    marginTop:    4,
  },

  /* card */
  card: {
    background:    "rgba(15,23,42,0.85)",
    backdropFilter:"blur(20px)",
    WebkitBackdropFilter:"blur(20px)",
    border:        "1px solid rgba(255,255,255,0.08)",
    borderRadius:  24,
    padding:       "28px 32px 24px",
    width:         "min(360px, 90vw)",
    display:       "flex",
    flexDirection: "column",
    alignItems:    "center",
    gap:           14,
    boxShadow:     "0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(34,211,238,0.05)",
    animation:     "lockFadeIn 0.5s ease 0.1s both",
  },

  /* icon */
  iconWrap: {
    width:          52,
    height:         52,
    borderRadius:   16,
    background:     "linear-gradient(135deg, #22d3ee, #6366f1)",
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
    boxShadow:      "0 4px 20px rgba(34,211,238,0.35)",
  },

  title: {
    margin:     0,
    color:      "#f1f5f9",
    fontSize:   18,
    fontWeight: 600,
  },

  subtitle: {
    margin:     0,
    color:      "#64748b",
    fontSize:   12,
    textAlign:  "center",
  },

  /* tabs */
  tabs: {
    display:         "flex",
    background:      "rgba(255,255,255,0.04)",
    borderRadius:    12,
    padding:         3,
    width:           "100%",
    gap:             3,
  },
  tabBtn: {
    flex:           1,
    border:         "none",
    borderRadius:   10,
    padding:        "8px 0",
    fontSize:       13,
    fontWeight:     500,
    cursor:         "pointer",
    color:          "#64748b",
    background:     "transparent",
    transition:     "all 0.2s",
  },
  tabActive: {
    background: "rgba(34,211,238,0.12)",
    color:      "#22d3ee",
    boxShadow:  "0 0 0 1px rgba(34,211,238,0.2)",
  },

  /* PIN dots */
  dotRow: {
    display:        "flex",
    gap:            12,
    justifyContent: "center",
    marginTop:      4,
  },
  pinDot: {
    width:        14,
    height:       14,
    borderRadius: "50%",
    border:       "2px solid rgba(148,163,184,0.35)",
    transition:   "all 0.15s ease",
    background:   "transparent",
  },
  pinDotFilled: {
    background: "#22d3ee",
    border:     "2px solid #22d3ee",
    boxShadow:  "0 0 8px rgba(34,211,238,0.5)",
  },

  /* numpad */
  numpad: {
    display:       "flex",
    flexDirection: "column",
    gap:           8,
    width:         "100%",
  },
  numRow: {
    display:        "flex",
    gap:            8,
    justifyContent: "center",
  },
  numBtn: {
    width:          70,
    height:         65,
    borderRadius:   67,
    border:         "1px solid rgba(255,255,255,0.06)",
    background:     "rgba(255,255,255,0.04)",
    display:        "flex",
    flexDirection:  "column",
    alignItems:     "center",
    justifyContent: "center",
    cursor:         "pointer",
    gap:            1,
    transition:     "all 0.15s",
    color:          "#f1f5f9",
  },
  numMain: {
    fontSize:   22,
    fontWeight: 400,
    lineHeight: 1,
    color:      "#e2e8f0",
  },
  numSub: {
    fontSize:      8,
    letterSpacing: "1px",
    color:         "#64748b",
    fontWeight:    500,
  },

  /* error */
  errorTxt: {
    margin:    0,
    color:     "#ef4444",
    fontSize:  13,
    textAlign: "center",
  },

  /* footer */
  footer: {
    textAlign: "center",
    marginTop: 4,
  },
  forgotLink: {
    color:          "#22d3ee",
    fontSize:       12,
    textDecoration: "none",
    display:        "block",
    marginBottom:   6,
  },
  hint: {
    color:    "#475569",
    fontSize: 11,
    margin:   0,
  },
  kbd: {
    background:   "rgba(255,255,255,0.08)",
    border:       "1px solid rgba(255,255,255,0.12)",
    borderRadius: 4,
    padding:      "1px 5px",
    fontSize:     10,
    color:        "#94a3b8",
  },

  /* ghost button (pattern clear) */
  ghostBtn: {
    background:   "transparent",
    border:       "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color:        "#64748b",
    fontSize:     12,
    padding:      "4px 14px",
    cursor:       "pointer",
  },
};
